import type { ZonedDateTime } from '@js-joda/core';
import type {
  SellerTransactionQuery_sellerProducts_SellerProductsQuery_sellerTransaction_InContractTransaction_currentStep_ClosingStep as ClosingStep,
  SellerTransactionQuery_sellerProducts_SellerProductsQuery_sellerTransaction_InContractTransaction_currentStep_ClosingStep_requiredTasks_RequiredClosingTasks_finalWalkthrough_FinalWalkthroughTask as FinalWalkthroughTask,
  SellerTransactionQuery_sellerProducts_SellerProductsQuery_sellerTransaction_InContractTransaction_currentStep_ClosingStep_requiredTasks_RequiredClosingTasks_lateCheckoutAddendum_DocumentSigningTask as LateCheckoutAddendumTask,
  SellerTransactionQuery_sellerProducts_SellerProductsQuery_sellerTransaction_InContractTransaction_currentStep_ClosingStep_optionalTasks_OptionalClosingTasks_lateCheckout_ClosingLateCheckoutTask as LateCheckoutTask,
  PartnershipReferralsFragment_SellerTransactionPartnershipReferrals_referralFlags_SellerTransactionReferralFlags as PartnershipFlags,
  PartnershipReferralsFragment,
  SellerTransactionSellDirectOfferFragment as SellDirectOffer,
  SellerDisclosureFragment,
  SellerTransactionQuery_sellerProducts_SellerProductsQuery_sellerTransaction as SellerTransaction,
} from '__generated__/graphql';
import {
  OdProtosSellCommon_Product as ProtoProduct,
  OdProtosSellSellerData_SellerExperience as SellerExperience,
} from '__generated__/graphql';
import type { AvailableProduct, SellerProducts } from 'sellDirect/declarations/products';
import { isAvailableProduct } from 'sellDirect/declarations/products';
import { systemZonedDateTimeFromStringOrNull } from 'shared/helpers/datetime';
export { SellerExperience };
export type { ClosingStep, SellDirectOffer };
import type { SellerLead } from 'sellDirect/graphql/SellerLeadContext';

type TransactionWithSellerExperience = Extract<
  SellerTransaction,
  { sellerExperience: SellerExperience }
>;

type TransactionWithPartnershipReferrals = Extract<
  SellerTransaction,
  { partnershipReferrals: PartnershipReferralsFragment | null | undefined }
>;

/** Type guard that returns true when the transaction exists and its sellerExperience is AGENT_ACQUISITION */
export const isAgentExperience = (
  sellerTransaction: SellerTransaction | null | undefined,
): sellerTransaction is TransactionWithSellerExperience =>
  sellerTransaction != null &&
  'sellerExperience' in sellerTransaction &&
  sellerTransaction.sellerExperience === SellerExperience.SELLER_EXPERIENCE_AGENT_ACQUISITION;

/** Type guard that returns true when the transaction exists and its sellerExperience is DIRECT_TO_CONSUMER */
export const isDirectToConsumerExperience = (
  sellerTransaction: SellerTransaction | null | undefined,
): sellerTransaction is TransactionWithSellerExperience =>
  sellerTransaction != null &&
  'sellerExperience' in sellerTransaction &&
  sellerTransaction.sellerExperience === SellerExperience.SELLER_EXPERIENCE_DIRECT_TO_CONSUMER;

/**
 * True when a qualified transaction is on a pre-contract step — the branch of the sell
 * index route that renders the direct-to-consumer landing page (contract-signing and
 * in-contract transactions render the final-offer/closing surfaces instead).
 *
 * The step list must stay in lockstep with the pre-contract case of `renderStepPage`
 * in `routes/dashboard+/_auth+/_sell+/sell+/($addressUUID)+/index.tsx`.
 */
export const isOnPreContractStep = (
  sellerTransaction: SellerTransaction | null | undefined,
): boolean => {
  if (sellerTransaction?.['__typename'] !== 'QualifiedTransaction') {
    return false;
  }
  const step = sellerTransaction.currentStep?.['__typename'];
  return step === 'AssessmentStep' || step === 'HoldingOfferStep' || step === 'UnderwritingStep';
};

/**
 * The Sell Direct offer for the transaction variants that carry one
 * (`QualifiedTransaction` and `InContractTransaction`); null otherwise.
 */
export const extractSellDirectOffer = (
  sellerTransaction: SellerTransaction | null | undefined,
): SellDirectOffer | null => {
  if (!sellerTransaction) {
    return null;
  }
  if (
    sellerTransaction['__typename'] === 'QualifiedTransaction' ||
    sellerTransaction['__typename'] === 'InContractTransaction'
  ) {
    return sellerTransaction.sellDirectOffer ?? null;
  }
  return null;
};

/** Type guard that returns true when the transaction exists and its partnershipReferrals is present */
const hasPartnershipReferrals = (
  sellerTransaction: SellerTransaction | null | undefined,
): sellerTransaction is TransactionWithPartnershipReferrals =>
  sellerTransaction != null && 'partnershipReferrals' in sellerTransaction;

/** Extracts blockBuyerContent from sellerTransaction.partnershipReferrals */
export const extractBlockBuyerContent = (
  sellerTransaction: SellerTransaction | null | undefined,
): boolean => {
  if (!hasPartnershipReferrals(sellerTransaction)) return false;
  return sellerTransaction.partnershipReferrals?.blockBuyerContent ?? false;
};

/** Extracts isPartnershipLead from sellerTransaction.partnershipReferrals */
export const extractIsPartnershipLead = (
  sellerTransaction: SellerTransaction | null | undefined,
): boolean => {
  if (!hasPartnershipReferrals(sellerTransaction)) return false;
  return sellerTransaction.partnershipReferrals?.isPartnershipLead ?? false;
};

/** Extracts referralFlags from sellerTransaction.partnershipReferrals */
export const extractPartnershipFlags = (
  sellerTransaction: SellerTransaction | null | undefined,
): PartnershipFlags | null => {
  if (!hasPartnershipReferrals(sellerTransaction)) return null;
  return sellerTransaction.partnershipReferrals?.referralFlags ?? null;
};

/** True when the sell-direct offer exists and is expired (no offer ⇒ nothing to expire). */
export const isOfferExpired = (sellDirectOffer: SellDirectOffer | null | undefined): boolean =>
  sellDirectOffer?.isExpired ?? false;

/**
 * @deprecaed Check `InContractTransaction` type instead
 * True once the purchase agreement is signed.
 */
export const hasSignedContract = (sellDirectOffer: SellDirectOffer | null | undefined): boolean =>
  !!sellDirectOffer &&
  (sellDirectOffer.isInContract ||
    sellDirectOffer.isWithdrawn ||
    sellDirectOffer.isClosed ||
    sellDirectOffer.isInLateCheckout);

/**
 * Parses `sellDirectOffer.expiresAt` into a system-zone {@link ZonedDateTime}.
 * Only meaningful for computing days-remaining / display dates when the offer isn't actually
 * expired — `isExpired` is the authoritative state flag; a past `expiresAt` alone must never
 * be read as "expired" (during a re-sign it's the prior purchase agreement's deadline).
 */
export const extractOfferExpiresAt = (
  sellDirectOffer: SellDirectOffer | null | undefined,
): ZonedDateTime | null => systemZonedDateTimeFromStringOrNull(sellDirectOffer?.expiresAt);

/** Parses `sellDirectOffer.expiredAt` into a system-zone {@link ZonedDateTime}. */
export const extractOfferExpiredAt = (
  sellDirectOffer: SellDirectOffer | null | undefined,
): ZonedDateTime | null => systemZonedDateTimeFromStringOrNull(sellDirectOffer?.expiredAt);

/** Parses `sellDirectOffer.sentAt` into a system-zone {@link ZonedDateTime}. */
export const extractOfferSentAt = (sellDirectOffer: SellDirectOffer): ZonedDateTime | null =>
  systemZonedDateTimeFromStringOrNull(sellDirectOffer.sentAt);

/** True when the offer is bucketed into the treatment group of the given experiment */
export const isInExperiment = (
  sellDirectOffer: SellDirectOffer | null | undefined,
  experimentKey: string,
): boolean =>
  sellDirectOffer?.experiments.find((entry) => entry.key === experimentKey)?.value === 'treatment';

/** Extracts the closing step from an in-contract transaction, if it is currently on one */
export const extractClosingStep = (
  sellerTransaction: SellerTransaction | null | undefined,
): ClosingStep | null => {
  if (sellerTransaction?.['__typename'] !== 'InContractTransaction') return null;
  const { currentStep } = sellerTransaction;
  return currentStep?.['__typename'] === 'ClosingStep' ? currentStep : null;
};

/** Extracts the disclose report from an in-contract transaction's closing disclosures task */
export const extractDisclosuresReport = (
  sellerTransaction: SellerTransaction | null | undefined,
): SellerDisclosureFragment | null =>
  extractClosingStep(sellerTransaction)?.requiredTasks.disclosures?.report ?? null;

/** Extracts the final-walkthrough (digital checkout) task from a closing step */
export const extractFinalWalkthrough = (
  closingStep: ClosingStep | null | undefined,
): FinalWalkthroughTask | null => closingStep?.requiredTasks.finalWalkthrough ?? null;

/** Extracts the late-checkout (LCO) request task from a closing step */
export const extractLateCheckout = (
  closingStep: ClosingStep | null | undefined,
): LateCheckoutTask | null => closingStep?.optionalTasks?.lateCheckout ?? null;

/** Extracts the late-checkout addendum signing task from a closing step */
export const extractLateCheckoutAddendum = (
  closingStep: ClosingStep | null | undefined,
): LateCheckoutAddendumTask | null => closingStep?.requiredTasks.lateCheckoutAddendum ?? null;

export function extractSellerLead(sellerTransaction: SellerTransaction): SellerLead;
export function extractSellerLead(sellerTransaction: null | undefined): null;
export function extractSellerLead(
  sellerTransaction: SellerTransaction | null | undefined,
): SellerLead | null;
export function extractSellerLead(
  sellerTransaction: SellerTransaction | null | undefined,
): SellerLead | null {
  if (!sellerTransaction) {
    return null;
  }

  return {
    ...sellerTransaction.sellerLead,
    address: sellerTransaction.address,
  };
}

const EMPTY_SELLER_PRODUCTS: SellerProducts = {
  availableProducts: [],
  selectedProduct: undefined,
  listingProduct: undefined,
  cashOfferProduct: undefined,
  cashPlusProduct: undefined,
  previouslyEligibleProducts: [],
};

/**
 * Extracts seller-product slots from `sellerTransaction.acquisitionProducts`,
 * `selectedAcquisitionProduct.kind`, and `previouslyEligibleProducts`.
 *
 * `availableProducts` lists displayable products only (Cash + Cash Plus); the
 * Listing slot has its own dedicated `listingProduct` field. `selectedProduct`
 * is filtered through `isAvailableProduct` so that LIST_WITH_CERTAINTY collapses
 * to undefined the way the legacy productsExperiences derivation did.
 */
export const extractSellerProducts = (
  sellerTransaction: SellerTransaction | null | undefined,
): SellerProducts => {
  if (!sellerTransaction || !('acquisitionProducts' in sellerTransaction)) {
    return EMPTY_SELLER_PRODUCTS;
  }

  const { sellDirect, sellWithUpside, listing } = sellerTransaction.acquisitionProducts;

  const availableProducts: AvailableProduct[] = [];
  if (sellDirect) {
    availableProducts.push(ProtoProduct.OPENDOOR_DIRECT);
  }
  if (sellWithUpside) {
    availableProducts.push(ProtoProduct.SELL_WITH_UPSIDE);
  }

  const selectedKind = sellerTransaction.selectedAcquisitionProduct?.kind;
  const selectedProduct =
    selectedKind && isAvailableProduct(selectedKind) ? selectedKind : undefined;

  // gql nulls -> interface uses optional `?:` (T | undefined); coerce at assignment.
  return {
    availableProducts,
    selectedProduct,
    listingProduct: listing ?? undefined,
    cashOfferProduct: sellDirect ?? undefined,
    cashPlusProduct: sellWithUpside ?? undefined,
    previouslyEligibleProducts: sellerTransaction.previouslyEligibleProducts ?? [],
  };
};
